import { api } from './api';
import { Risorsa } from '../types';

/**
 * Request interface for creating/updating a Risorsa
 */
export interface RisorsaRequest {
  nome: string;
  tipo: 'contanti' | 'conto_corrente' | 'carta' | 'altro';
  descrizione?: string;
  isActive?: boolean;
  saldoIniziale?: number;
}

/**
 * Filter interface for querying Risorse
 */
export interface RisorsaFilters {
  tipo?: 'contanti' | 'conto_corrente' | 'carta' | 'altro';
}

/**
 * Backend response format (different from frontend Risorsa type)
 */
interface RisorsaBackend {
  id: number;
  nome: string;
  tipo: string;
  descrizione?: string;
  isActive: boolean;
  saldoIniziale: number;
  saldoCorrente: number;
  daIncassare?: number;  // Entrate non pagate
  daPagare?: number;     // Uscite non pagate
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Transform backend response to frontend Risorsa type
 */
const mapBackendToFrontend = (backend: RisorsaBackend): Risorsa => {
  return {
    id: backend.id,
    nome: backend.nome,
    tipo: (backend.tipo as Risorsa['tipo']) || 'altro',
    descrizione: backend.descrizione,
    attiva: backend.isActive,
    saldo_iniziale: backend.saldoIniziale,
    saldo_corrente: backend.saldoCorrente,
    da_incassare: backend.daIncassare || 0,
    da_pagare: backend.daPagare || 0,
    utente_id: 0,
  };
};

/**
 * Get all Risorse with optional filters
 */
const getRisorse = async (filters?: RisorsaFilters): Promise<Risorsa[]> => {
  const params = new URLSearchParams();

  if (filters?.tipo) {
    params.append('tipo', filters.tipo);
  }

  const queryString = params.toString();
  const url = queryString ? `/risorse?${queryString}` : '/risorse';

  const response = await api.get<RisorsaBackend[]>(url);
  return response.data.map(mapBackendToFrontend);
};

/**
 * Get a single Risorsa by ID
 */
const getRisorsaById = async (id: number): Promise<Risorsa> => {
  const response = await api.get<RisorsaBackend>(`/risorse/${id}`);
  return mapBackendToFrontend(response.data);
};

/**
 * Create a new Risorsa
 */
const createRisorsa = async (data: RisorsaRequest): Promise<Risorsa> => {
  const response = await api.post<RisorsaBackend>('/risorse', data);
  return mapBackendToFrontend(response.data);
};

/**
 * Update an existing Risorsa
 */
const updateRisorsa = async (id: number, data: RisorsaRequest): Promise<Risorsa> => {
  const response = await api.put<RisorsaBackend>(`/risorse/${id}`, data);
  return mapBackendToFrontend(response.data);
};

/**
 * Delete a Risorsa
 */
const deleteRisorsa = async (id: number): Promise<void> => {
  await api.delete(`/risorse/${id}`);
};

/**
 * Export Risorse to CSV
 */
const exportRisorse = async (): Promise<void> => {
  const response = await api.get('/risorse/export', {
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const contentDisposition = response.headers['content-disposition'];
  let filename = 'risorse.csv';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const risorseService = {
  getRisorse,
  getRisorsaById,
  createRisorsa,
  updateRisorsa,
  deleteRisorsa,
  exportRisorse,
};

export default risorseService;
